/* eslint-disable arrow-body-style */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Bubble, Button, Segment } from 'tea-component';
import { useLocal } from '../../../../../utils/customHook';
import { getLocale, useIntl, useSelector, history } from 'umi';
import { getExplainVal } from '../../../../../utils/getExplainVal';
import { FilterContext } from '../../../../../components/PageContainer/context';
import { dynamicString } from '@home/utils/dynamicString';
import { getName } from '@home/utils/common';

import './index.scss';

/**
 * 解析指标名称/explain/formatter，支持 dynamicString 模板
 * enName/cnName 支持 "{{metricsInfo.xxx.en}}" 格式的模板字符串
 * explain 支持同样的模板字符串解析
 * formatter 可从 metricsInfo 获取
 */
const resolveField = (value: string | undefined, dynamicData: Record<string, any>): string => {
  if (!value) return '';
  return dynamicString(value, dynamicData, value) || value;
};

const SegmentMoreCo = (props: any) => {
  const { config, children, style, data, changeData, ...restProps } = props;
  const {
    componentSegment,
    defaultInicator = '',
    showExpand = true,
    reqMetrics: configReqMetrics,
  } = config || {};
  const metricsInfo = data?.metricsInfo;
  // 优先从 config.reqMetrics 读，fallback 到 data.reqMetrics（统一配置在外层 data 中）
  const reqMetrics = configReqMetrics || data?.reqMetrics;
  const { filterVal } = useContext(FilterContext);
  const timeType = filterVal?.statisticDate_timeType;
  const { isEN } = useLocal();
  const { formatMessage } = useIntl();

  const { explainInfo } = useSelector((state: any) => {
    return {
      explainInfo: state.dashboard?.explainInfo,
      gameCode: state?.dashboard?.currentGame?.game_code,
    };
  });

  // 构建用于 dynamicString 的数据源
  const dynamicData = useMemo(() => ({
    metricsInfo,
  }), [metricsInfo]);

  /**
   * 解析 reqMetrics 配置，支持两种格式：
   * - 数组: ["a","b"] → 所有粒度通用
   * - 对象: { Day: ["a"], Week: ["b"], Month: ["c"] } → 按粒度
   */
  const metricsFilter = useMemo(() => {
    if (!reqMetrics) return null;
    if (Array.isArray(reqMetrics)) return reqMetrics;
    const currentType = timeType || 'Day';
    return reqMetrics[currentType] || reqMetrics.Day || null;
  }, [reqMetrics, timeType]);

  // 根据 reqMetrics 过滤并按其顺序排列可见指标
  const selectedTimeSegment = useMemo(() => {
    const currentType = timeType || 'Day';
    const segmentList = componentSegment?.[currentType] || componentSegment?.Day || [];
    if (!metricsFilter || !Array.isArray(metricsFilter) || metricsFilter.length === 0) {
      return segmentList;
    }
    // 创建 metric_key/value 到指标配置的映射
    const metricsMap = new Map<string, any>();
    segmentList.forEach((item: any) => {
      metricsMap.set(item.metric_key, item);
      metricsMap.set(item.value, item);
    });
    // 按 reqMetrics 的顺序重建数组
    return metricsFilter
      .map((key: string) => metricsMap.get(key))
      .filter((item: any) => item !== undefined);
  }, [componentSegment, timeType, metricsFilter]);

  const historyParam = history?.location?.state;

  const [isExpanded, setIsExpanded] = useState(!!historyParam);
  const handleClick = () => {
    setIsExpanded(!isExpanded);
  };
  // 其他页面跳转至相关tab处理
  const indicators = historyParam ? JSON.parse(historyParam)?.indicators : defaultInicator;
  const initialSelectedSegment = indicators ? selectedTimeSegment?.find((item: { value: string; }) => item?.value?.toLocaleLowerCase() === indicators?.toLocaleLowerCase()) : undefined;
  const defaultValue = initialSelectedSegment ? initialSelectedSegment.value : selectedTimeSegment?.[0]?.value;
  // 初始 selectedSegment 状态
  const [selectedSegment, setSelectedSegment] = useState(defaultValue);

  useEffect(() => {
    const propsMetrics = selectedTimeSegment?.find((item: any) => item?.value === selectedSegment);
    if (propsMetrics) {
      const resolvedFormatter = resolveField(propsMetrics.formatter, dynamicData);
      changeData('metrics', {
        ...propsMetrics,
        ...(resolvedFormatter ? { resolvedFormatter } : {}),
      });
    }
  }, [selectedSegment, selectedTimeSegment]);


  useEffect(() => {
    setSelectedSegment(defaultValue);
  }, [defaultValue]);

  const options = useMemo(() => {
    return selectedTimeSegment.map((item: any) => {
      const { enName, cnName, rainbowKey, value, disable, explainEn, explainCn } = item;
      // 通过 dynamicString 解析模板字符串，支持 "{{metricsInfo.xxx.en}}" 格式
      const resolvedEnName = resolveField(enName, dynamicData);
      const resolvedCnName = resolveField(cnName, dynamicData);
      const resolvedExplainEn = resolveField(explainEn, dynamicData);
      const resolvedExplainCn = resolveField(explainCn, dynamicData);

      const explain = getExplainVal(explainInfo, rainbowKey, getLocale(), resolvedExplainEn, resolvedExplainCn);
      const displayName = getName(isEN, resolvedEnName, resolvedCnName) || (isEN ? enName : cnName);

      return {
        text: (
          <Bubble
            content={
              explain ? <p className="db-SwitchChart__titleBubble-infoTxt" dangerouslySetInnerHTML={{ __html: explain?.replaceAll('\n', '<br/>') }} /> : ''
            }
            dark
            className="metrics-segment__item-bubble"
            placement="top-start"
          >
            {displayName}
          </Bubble>
        ),
        value,
        disabled: disable !== undefined ? disable : false,
      };
    });
  }, [isEN, selectedSegment, explainInfo, timeType, selectedTimeSegment, dynamicData]);


  useEffect(() => {
    // Check if selectedSegment is in options
    const isValidSegment = options.some((option: { value: string; }) => option.value === selectedSegment);
    if (!isValidSegment) {
      setSelectedSegment(defaultValue);
    }
  }, [options, selectedSegment, defaultValue]);

  // 处理段选择变化
  const handleSegmentChange = (value: string) => {
    setSelectedSegment(value);
    if (value === 'churnUsers') {
      changeData('haveChurnLevel', true);
    } else {
      changeData('haveChurnLevel', false);
    }
  };

  return (
    <>
      <div className="dbd-segment-more-flex" style={style}>
        <Segment
          className={`segment-light ${isExpanded ? 'show' : 'hide'}`}
          options={options}
          value={selectedSegment}
          onChange={(value) => handleSegmentChange(value)}
        />
        {showExpand && (
          <Button
            className="dateComparison-flex-btn"
            type="link"
            onClick={handleClick}
          >
            <span className="button-text">
              {isExpanded
                ? formatMessage({ id: 'comparison_analysis_date_comparison_key_metrics' })
                : formatMessage({ id: 'comparison_analysis_date_comparison_more_metrics' })}
            </span>
          </Button>
        )}
      </div>
      <div>
        {React.Children.map(children, (child) => {
          return React.cloneElement(child, {
            ...restProps,
            data,
            changeData,
            selectedTimeSegment,
            selectedSegment,
          });
        })}
      </div>
    </>
  );
};

export default SegmentMoreCo;